/**
 * Teseu — camada de eventos e checkout do tema.
 *
 * Tudo que acontece no fluxo de compra passa por aqui antes de sair da loja,
 * para rastreamento e regras próprias. API pública em window.Teseu:
 *
 *   Teseu.on('add_to_cart', (payload) => {})   ouve um evento (também via document 'teseu:add_to_cart')
 *   Teseu.track('nome', { ... })              dispara um evento próprio
 *   Teseu.checkout.before(async (ctx) => {})  roda antes de ir ao checkout (limite de 1,2 s por hook)
 *       ctx.source  'cart-drawer' | 'cart-page' | 'buy-now'
 *       ctx.cart    carrinho (/cart.js), ctx.items itens no formato GA4, ctx.value total
 *       ctx.params  parâmetros extras para a URL do checkout (ex.: ctx.params.discount = 'CUPOM')
 *       ctx.url     URL alternativa; ctx.cancel = true interrompe (ex.: abrir um modal)
 *   Teseu.checkout.go({ params })             leva ao checkout aplicando os hooks
 *
 * Eventos: view_item, select_variant, add_to_cart, buy_now, begin_checkout.
 * Com "Controlar eventos e checkout pelo tema" desligado nada é interceptado nem disparado;
 * o botão "Comprar agora" só adiciona ao carrinho e vai ao checkout.
 * Com "Enviar eventos ao dataLayer" ligado, view_item, add_to_cart e begin_checkout
 * também entram em window.dataLayer no formato de e-commerce do GA4 (para o Google Tag Manager).
 */
(() => {
  if (window.Teseu && window.Teseu.checkout) return;
  const Teseu = (window.Teseu = window.Teseu || {});
  const cfg = Teseu.config || {};
  const enabled = cfg.enabled !== false;
  const hooks = [];
  const listeners = {};
  const HOOK_TIMEOUT = 1200;
  const CART_TIMEOUT = 1500;
  const DATA_LAYER_EVENTS = new Set(['view_item', 'add_to_cart', 'begin_checkout']);

  const money = (cents) => Math.round(Number(cents) || 0) / 100;
  const currency = () => cfg.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
  const withTimeout = (promise, ms) => Promise.race([promise, new Promise((resolve) => setTimeout(resolve, ms))]);

  const itemFromLine = (line, quantity) => ({
    item_id: line.sku || String(line.variant_id || line.id),
    item_name: line.product_title || line.title,
    item_variant: line.variant_title || undefined,
    item_brand: line.vendor || undefined,
    item_category: line.product_type || undefined,
    price: money(line.final_price != null ? line.final_price : line.price),
    quantity: quantity || line.quantity || 1,
    product_id: line.product_id,
    variant_id: line.variant_id || line.id,
  });

  const sum = (items) => Math.round(items.reduce((total, item) => total + item.price * item.quantity, 0) * 100) / 100;

  const track = (name, detail) => {
    if (!enabled) return null;
    const payload = Object.assign({}, detail || {}, { currency: (detail && detail.currency) || currency(), timestamp: Date.now() });
    document.dispatchEvent(new CustomEvent('teseu:' + name, { detail: payload, bubbles: true }));
    (listeners[name] || []).forEach((fn) => {
      try {
        fn(payload);
      } catch (error) {
        console.error('[Teseu] listener', name, error);
      }
    });
    if (cfg.dataLayer && DATA_LAYER_EVENTS.has(name)) {
      window.dataLayer = window.dataLayer || [];
      const ecommerce = { currency: payload.currency };
      if (payload.value != null) ecommerce.value = payload.value;
      if (payload.items) ecommerce.items = payload.items;
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({ event: name, ecommerce, teseu: { source: payload.source || 'theme' } });
    }
    return payload;
  };

  const on = (name, fn) => {
    (listeners[name] = listeners[name] || []).push(fn);
    return () => {
      listeners[name] = (listeners[name] || []).filter((item) => item !== fn);
    };
  };

  const trackAdd = (response, source) => {
    if (!response) return;
    const lines = response.items || [response];
    if (!lines.length || !lines[0] || !lines[0].id) return;
    const items = lines.map((line) => itemFromLine(line));
    return track('add_to_cart', { source: source || 'product-form', items, value: sum(items) });
  };

  const fetchCart = () =>
    fetch(((window.routes && window.routes.cart_url) || '/cart') + '.js', { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

  const buildUrl = (ctx) => {
    const url = new URL(ctx.url || cfg.checkoutUrl || '/checkout', window.location.origin);
    new URLSearchParams(cfg.checkoutParams || '').forEach((value, key) => url.searchParams.set(key, value));
    Object.entries(ctx.params || {}).forEach(([key, value]) => {
      if (value != null && value !== '') url.searchParams.set(key, value);
    });
    return url.toString();
  };

  const setLoading = (button, loading) => {
    if (!button) return;
    button.classList.toggle('loading', loading);
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !loading);
  };

  async function beginCheckout(ctx) {
    ctx.params = ctx.params || {};
    ctx.url = ctx.url || null;
    ctx.cancel = false;
    if (!enabled) {
      ctx.items = [];
      ctx.navigate = Boolean(ctx.url || cfg.checkoutParams || Object.keys(ctx.params).length);
      return ctx;
    }
    if (!ctx.cart) ctx.cart = (await withTimeout(fetchCart(), CART_TIMEOUT)) || null;
    ctx.items = ctx.cart && ctx.cart.items ? ctx.cart.items.map((line) => itemFromLine(line)) : [];
    ctx.value = ctx.cart ? money(ctx.cart.total_price) : sum(ctx.items);
    track('begin_checkout', { source: ctx.source, items: ctx.items, value: ctx.value, cart: ctx.cart });
    for (const hook of hooks) {
      try {
        await withTimeout(Promise.resolve(hook(ctx)), HOOK_TIMEOUT);
      } catch (error) {
        console.error('[Teseu] checkout hook', error);
      }
      if (ctx.cancel) break;
    }
    ctx.navigate = Boolean(ctx.url || cfg.checkoutParams || Object.keys(ctx.params).length);
    return ctx;
  }

  const go = (options) =>
    beginCheckout(Object.assign({ source: 'custom' }, options || {})).then((ctx) => {
      if (!ctx.cancel) window.location.assign(buildUrl(ctx));
      return ctx;
    });

  // Botões "Finalizar compra" do carrinho (gaveta e página)
  document.addEventListener(
    'click',
    (event) => {
      if (!enabled) return;
      const button = event.target.closest && event.target.closest('button[name="checkout"], [data-teseu-checkout]');
      if (!button || button.disabled || button.dataset.teseuPass === '1') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setLoading(button, true);
      const source = button.closest('cart-drawer') ? 'cart-drawer' : 'cart-page';
      beginCheckout({ source, button }).then((ctx) => {
        if (ctx.cancel) return setLoading(button, false);
        if (ctx.navigate) return window.location.assign(buildUrl(ctx));
        button.dataset.teseuPass = '1';
        button.click();
        delete button.dataset.teseuPass;
        setTimeout(() => setLoading(button, false), 4000);
      });
    },
    true
  );

  // Botão "Comprar agora" do tema: adiciona ao carrinho e vai ao checkout
  async function buyNow(button) {
    const productForm = button.closest('product-form');
    const form = (productForm && productForm.querySelector('form')) || button.closest('form');
    if (!form) return;
    setLoading(button, true);
    if (productForm && productForm.handleErrorMessage) productForm.handleErrorMessage();
    const body = new FormData(form);
    body.delete('sections');
    body.delete('sections_url');
    const variantId = body.get('id');
    const quantity = parseInt(body.get('quantity'), 10) || 1;
    track('buy_now', { source: 'product', variant_id: variantId, quantity });
    try {
      const response = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await response.json();
      if (data.status) {
        if (productForm && productForm.handleErrorMessage) productForm.handleErrorMessage(data.description || data.message);
        return setLoading(button, false);
      }
      trackAdd(data, 'buy-now');
      const ctx = await beginCheckout({ source: 'buy-now', button });
      if (ctx.cancel) return setLoading(button, false);
      window.location.assign(buildUrl(ctx));
    } catch (error) {
      console.error('[Teseu] buy now', error);
      if (productForm && productForm.handleErrorMessage) productForm.handleErrorMessage(window.cartStrings && window.cartStrings.error);
      setLoading(button, false);
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('[data-teseu-buy-now]');
    if (!button || button.disabled || button.classList.contains('loading')) return;
    event.preventDefault();
    buyNow(button);
  });

  const initPubSub = () => {
    if (typeof subscribe !== 'function' || !window.PUB_SUB_EVENTS) return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.source !== 'product-form') return;
      trackAdd(event.cartData, 'product-form');
    });
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event && event.data && event.data.variant;
      if (!variant) return;
      if (Teseu.product) {
        Teseu.product.variant_id = variant.id;
        Teseu.product.item_variant = variant.title;
        Teseu.product.item_id = variant.sku || String(variant.id);
        Teseu.product.price = money(variant.price);
      }
      track('select_variant', {
        source: 'product',
        variant_id: variant.id,
        item_variant: variant.title,
        sku: variant.sku,
        price: money(variant.price),
        available: variant.available,
      });
    });
  };
  if (typeof subscribe === 'function') initPubSub();
  else document.addEventListener('DOMContentLoaded', initPubSub, { once: true });

  Teseu.track = track;
  Teseu.on = on;
  Teseu.trackAdd = trackAdd;
  Teseu.itemFromLine = itemFromLine;
  Teseu.checkout = {
    before: (fn) => {
      if (typeof fn === 'function') hooks.push(fn);
      return () => {
        const index = hooks.indexOf(fn);
        if (index > -1) hooks.splice(index, 1);
      };
    },
    go,
    url: (ctx) => buildUrl(ctx || {}),
  };
  document.dispatchEvent(new CustomEvent('teseu:ready', { detail: Teseu }));

  // Página de produto: view_item e troca de variante
  const productDataNode = document.querySelector('script[data-teseu-product]');
  if (productDataNode) {
    try {
      const item = JSON.parse(productDataNode.textContent);
      Teseu.product = item;
      track('view_item', { source: 'product', items: [Object.assign({ quantity: 1 }, item)], value: item.price, currency: item.currency });
    } catch (error) {
      console.error('[Teseu] product data', error);
    }
  }
})();
